import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { keyCode } from '@bingads-webui/keycode';

import {
  AssetLibraryLandingPageContent,
  AssetLibraryImageAssetsGridCellBase,
  AssetLibraryFoldersGridCell,
} from '@bingads-webui-campaign-react/asset-library-landing-page-content';
import {
  GridSectionName,
  keyDownHandlerForListItem,
  RealUsedFlagSuffix,
  handleRealUsedFlag,
  addAssetLibrarySurveyTriggerFlag,
  AssetLibrarySurveyTriggerFlag,
} from '@bingads-webui-campaign-react/asset-library-model';

import { useImageDeleteConfirmDialog } from './image-delete-confirm-dialog';

import { getAssetLibraryStyles } from './styles';

const filterTokens = { childrenGap: '4 12' };

const MAX_FOLDER_FETCH_LIMIT = 20;

export const AssetLibraryImagesView = (props) => {
  const {
    ianaTimeZone,
    assetLibraryUserPreferences,
    assetLibraryAccountPreferences,
    logger,
    dateFormat,
    i18n,
    onImageSelected,
    onFailedToLoadImages,
    multiSelectionEnabled,
    dataService,
    setShowApplyButton,
    onImagesSelected,
    triggerAddImagesRef,
    maxUploadNum,
    setErrors,
    setDisableApplyButton,
    setSelectionInfoText,
    allowedAssetType,
    enableImageDeleting,
    isAssetLibraryAIGCMVPEnabled,
    isClickableOutsideFocusTrap,
    disabledAssetSources,
  } = props;

  // Only supports maximum of adding 20 images at a time in a folder. Please contact MT to raise limit on API first
  const boundedMaxUploadNum = maxUploadNum > MAX_FOLDER_FETCH_LIMIT ? MAX_FOLDER_FETCH_LIMIT : maxUploadNum;

  // show counter at 0 to start
  useEffect(() => {
    setSelectionInfoText(i18n.getString(_TL_('0 of {{limitNumber}} images selected'), { limitNumber: boundedMaxUploadNum }));
  }, [i18n, boundedMaxUploadNum, setSelectionInfoText]);

  const classes = useDefaultStyles(getAssetLibraryStyles);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [dataLoadedOnce, setDataLoadedOnce] = useState(false);

  useEffect(() => {
    if (dataLoadedOnce) {
      addAssetLibrarySurveyTriggerFlag(assetLibraryAccountPreferences, AssetLibrarySurveyTriggerFlag.Picker, RealUsedFlagSuffix.Picker);
    }
  }, [assetLibraryAccountPreferences, dataLoadedOnce]);

  const dataLoaded = useCallback(() => {
    setDataLoadedOnce(() => true);
  }, []);

  const onApplyImages = useCallback(() => {
    if (selectedCount <= boundedMaxUploadNum) {
      const imagesFromFolders = _.flatten(_.map(selectedFolders, folder => folder.assets));
      const imagesIdsFromFolders = _.map(imagesFromFolders, ({ id }) => id);
      const selectedImageIds = _.map(selectedImages, ({ id }) => id);
      const selectedImagePerformanceLabels = _.map(selectedImages, ({ performanceLabel }) => performanceLabel);
      const imagesToSelect = [
        ...imagesIdsFromFolders,
        ...selectedImageIds,
      ];

      onImagesSelected(imagesToSelect);
      logger.logTrace(
        'Select images from asset library in ad creation flow',
        { imageIds: imagesToSelect.join(','), imagePerformanceLabels: selectedImagePerformanceLabels.join(','), length: imagesToSelect.length }
      );
      handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.Picker);
    }
  }, [selectedFolders, selectedImages, onImagesSelected, selectedCount, boundedMaxUploadNum, logger, assetLibraryAccountPreferences]);

  triggerAddImagesRef.current = onApplyImages;

  const [reload, setReload] = useState(undefined);
  const forceToReload = useCallback(() => setReload({}), []);
  const imageDeleteConfirmDialog = useImageDeleteConfirmDialog({
    dataService, i18n, onDeleted: forceToReload,
  });

  const renderImageGridCell = useCallback(({ item, searchText }) => {
    const onClick = () => {
      logger.logTrace('Selected Asset Library images', { imageId: item && item.id });
      if (onImageSelected) {
        onImageSelected(item);
      }
    };

    const onKeyDown = (event) => {
      if (event.keyCode === keyCode.Enter) {
        onClick();
      }
    };
    const renderCellContent = defaultContent => (
      <div onClick={onClick} role="button" tabIndex={0} onKeyDown={onKeyDown} style={{ width: '100%', height: '100%' }}>
        {defaultContent}
      </div>
    );
    return (
      <AssetLibraryImageAssetsGridCellBase
        item={item}
        i18n={i18n}
        highlightedText={searchText}
        renderCellContent={renderCellContent}
        deletable={enableImageDeleting}
        onDelete={imageDeleteConfirmDialog.open}
      />
    );
  }, [enableImageDeleting, i18n, imageDeleteConfirmDialog.open, logger, onImageSelected]);

  // reset selection state when changing folders
  const resetSelectionState = useCallback(() => {
    setSelectedFolders([]);
    setSelectedImages([]);
    setSelectedCount(0);
    setShowApplyButton(false);
    setErrors([]);
    setSelectionInfoText(i18n.getString(_TL_('0 of {{limitNumber}} images selected'), { limitNumber: boundedMaxUploadNum }));
    setDisableApplyButton(false);
  }, [setShowApplyButton, setErrors, setSelectionInfoText, setDisableApplyButton, i18n, boundedMaxUploadNum]);

  const {
    navigateIntoFolder,
  } = dataService;

  const renderMultiSelectionGridCell = useCallback(({
    item, selected, selectItem, searchText, sectionName, index,
  }) => {
    const onClick = () => {
      selectItem(!selected);
    };

    const openFolder = (folderItem) => {
      const { folderId } = folderItem || {};
      if (folderId) {
        resetSelectionState();
        navigateIntoFolder({ id: folderId });
        logger.logTrace('Navigate into folder in ad creation flow', { folderId });
      }
    };
    const assetCells = document.querySelectorAll('.tiles-grid-cell-container');

    let cell = null;
    if (sectionName === GridSectionName.Folder) { // folder cell
      cell = (
        <AssetLibraryFoldersGridCell
          item={item}
          highlightedText={searchText}
          i18n={i18n}
          selected={selected}
          selectable
          onSelect={onClick}
          onFolderClick={openFolder}
          allowedAssetType={allowedAssetType}
        />
      );
    } else { // image cell
      const customTabHandler = (shiftKeyPressed) => {
        const apply = document.querySelector('.apply-button');
        const cancel = document.querySelector('.cancel-button');
        const sortBy = document.querySelector('.asset-library-sort .ms-Dropdown');
        // move focus backforward if shift key is pressed
        const target = shiftKeyPressed ? sortBy : (apply || cancel);
        if (target) {
          target.focus();
        }
      };
      const onKeyDown = (event, i) => {
        keyDownHandlerForListItem(event, i, assetCells, undefined, onClick, onClick, customTabHandler);
      };

      cell = (
        <div
          className="tiles-grid-cell-container"
          onKeyDown={(event) => { onKeyDown(event, index); }}
          style={{ width: '100%', height: '100%' }}
          data-is-focusable
          role="checkbox"
          aria-checked={selected}
          tabIndex={-1}
        >
          <AssetLibraryImageAssetsGridCellBase
            item={item}
            highlightedText={searchText}
            i18n={i18n}
            selectable
            onClick={onClick}
            selected={selected}
            deletable={enableImageDeleting}
            onDelete={imageDeleteConfirmDialog.open}
          />
        </div>
      );
    }
    return cell;
  }, [allowedAssetType, enableImageDeleting, i18n, imageDeleteConfirmDialog.open, logger, navigateIntoFolder, resetSelectionState]);

  const onSelectionChange = useCallback(({
    sectionName, selectedItems,
  }) => {
    let newCount = 0;

    if (sectionName === GridSectionName.Folder) {
      setSelectedFolders([
        ...selectedItems,
      ]);
      newCount = selectedImages.length + _.reduce(selectedItems, (memo, { itemCount }) => memo + itemCount.ImageAssetsCount, 0);
    } else if (sectionName === GridSectionName.Asset) {
      setSelectedImages([
        ...selectedItems,
      ]);
      newCount = selectedItems.length + _.reduce(selectedFolders, (memo, { itemCount }) => memo + itemCount.ImageAssetsCount, 0);
    }

    if (newCount > boundedMaxUploadNum) {
      setErrors([i18n.getString(_TL_('You\'ve exceeded the max. number of assets you can add at a time ({{limitNumber}}). Please try again.'), { limitNumber: boundedMaxUploadNum })]);
      setDisableApplyButton(true);
    } else {
      setErrors([]);
      setDisableApplyButton(false);
    }

    const singularImageString = _TL_('{{selectedNumber}} of {{limitNumber}} image selected');
    const pluralImagesString = _TL_('{{selectedNumber}} of {{limitNumber}} images selected');
    const stringToUse = boundedMaxUploadNum > 1 ? pluralImagesString : singularImageString;
    setSelectionInfoText(i18n.getString(stringToUse, { selectedNumber: newCount, limitNumber: boundedMaxUploadNum }));

    setSelectedCount(newCount);
    setShowApplyButton(newCount > 0);
  }, [
    setShowApplyButton,
    selectedImages,
    selectedFolders,
    boundedMaxUploadNum,
    i18n,
    setErrors,
    setDisableApplyButton,
    setSelectionInfoText,
  ]);

  const sectionSelectedItems = useMemo(() => ({
    [GridSectionName.Asset]: selectedImages,
    [GridSectionName.Folder]: selectedFolders,
  }), [selectedFolders, selectedImages]);

  return (
    <>
      <AssetLibraryLandingPageContent
        dataService={dataService}
        renderGridCell={multiSelectionEnabled ? renderMultiSelectionGridCell : renderImageGridCell}
        dateFormat={dateFormat}
        ianaTimeZone={ianaTimeZone}
        assetLibraryUserPreferences={assetLibraryUserPreferences}
        assetLibraryAccountPreferences={assetLibraryAccountPreferences}
        logger={logger}
        i18n={i18n}
        dropdownWidth="auto"
        tokens={filterTokens}
        onFailedToLoadData={onFailedToLoadImages}
        displayFolderPath
        multiSelectionEnabled={multiSelectionEnabled}
        onSelectionChange={onSelectionChange}
        sectionSelectedItems={sectionSelectedItems}
        classes={classes}
        assetsToLoadForSubfolders={boundedMaxUploadNum}
        onFolderNavigationChange={resetSelectionState}
        allowedAssetType={allowedAssetType}
        reload={reload}
        realUsedFlagSuffix={RealUsedFlagSuffix.Picker}
        dataLoaded={dataLoaded}
        isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCMVPEnabled}
        isClickableOutsideFocusTrap={isClickableOutsideFocusTrap}
        disabledAssetSources={disabledAssetSources}
      />
      {imageDeleteConfirmDialog.render()}
    </>
  );
};

AssetLibraryImagesView.propTypes = {
  ianaTimeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  assetLibraryUserPreferences: PropTypes.shape({}).isRequired,
  assetLibraryAccountPreferences: PropTypes.shape({}),
  logger: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  onImageSelected: PropTypes.func,
  onFailedToLoadImages: PropTypes.func,
  multiSelectionEnabled: PropTypes.bool,
  dataService: PropTypes.shape({
    navigateIntoFolder: PropTypes.func,
  }).isRequired,
  setShowApplyButton: PropTypes.func.isRequired,
  onImagesSelected: PropTypes.func.isRequired,
  triggerAddImagesRef: PropTypes.shape({}).isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  setErrors: PropTypes.func.isRequired,
  setDisableApplyButton: PropTypes.func.isRequired,
  setSelectionInfoText: PropTypes.func.isRequired,
  allowedAssetType: PropTypes.string,
  enableImageDeleting: PropTypes.bool,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool,
  isClickableOutsideFocusTrap: PropTypes.bool,
  disabledAssetSources: PropTypes.arrayOf(PropTypes.string),
};

AssetLibraryImagesView.defaultProps = {
  onImageSelected: undefined,
  onFailedToLoadImages: undefined,
  multiSelectionEnabled: false,
  allowedAssetType: null,
  enableImageDeleting: false,
  assetLibraryAccountPreferences: null,
  isAssetLibraryAIGCMVPEnabled: false,
  isClickableOutsideFocusTrap: false,
  disabledAssetSources: [],
};
